import React, { useEffect } from 'react';
import cn from 'classnames';
import { useTranslation } from 'client/hooks';
import Icon from 'client/components/common/Icon';
import PopupWrapper from 'client/components/common/Popups/PopupWrapper';
import TransitionComponent from 'client/components/common/TransitionComponent';

import css from './NetworkStatusPopup.module.scss';
import { useCountdown } from 'usehooks-ts';

export enum NETWORK_STATUS {
  ONLINE = 'online',
  OFFLINE = 'offline',
  RESTORED = 'restored', // connection was offline, but became online
}

interface Props {
  status: NETWORK_STATUS;
}

const PAGE_RELOAD_INTERVAL = 3000;

const NetworkStatusPopup: React.FC<Props> = ({ status }) => {
  const { translate } = useTranslation('popUps');
  const isRestored = status === NETWORK_STATUS.RESTORED;
  const [timeLeft, { startCountdown, stopCountdown, resetCountdown }] = useCountdown({
    countStart: PAGE_RELOAD_INTERVAL / 1000,
  });

  // control countdown on status change
  useEffect(() => {
    if (isRestored) {
      startCountdown();
    } else {
      stopCountdown();
      resetCountdown();
    }
  }, [isRestored, startCountdown, stopCountdown, resetCountdown]);

  // restart application
  useEffect(() => {
    if (isRestored && timeLeft === 0) {
      window.location.reload();
    }
  }, [isRestored, timeLeft]);

  return (
    <TransitionComponent type="opacity" visible={status === NETWORK_STATUS.OFFLINE || isRestored}>
      <PopupWrapper withCloseIcon={false} className={cn('highestZIndex', 'bottom-0')} showOverlay>
        {status === NETWORK_STATUS.ONLINE ? (
          <React.Fragment />
        ) : (
          <div className={css.container}>
            <div className={css.icon}>
              <Icon type={status === NETWORK_STATUS.OFFLINE ? 'broadcast-end-l' : 'broadcast-start-l'} />
            </div>
            <div className={css.title}>{translate(`networkStatusPopup.${status}.title`)}</div>
            <div className={css.text}>{translate(`networkStatusPopup.${status}.text`)}</div>
            <TransitionComponent key={`tl-${timeLeft}`} type="opacity" visible={isRestored}>
              <div className={css.text}>{timeLeft}</div>
            </TransitionComponent>
          </div>
        )}
      </PopupWrapper>
    </TransitionComponent>
  );
};

export default NetworkStatusPopup;
